import React, { useState } from 'react';
import { SearchIcon, FilterIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, EditIcon, CheckIcon, TrashIcon, DownloadIcon, XIcon } from 'lucide-react';
interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
}
interface Filter {
  key: string;
  label: string;
  options: string[];
}
interface DataTableProps {
  columns: Column[];
  data: any[];
  filters?: Filter[];
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onApprove?: (row: any) => void;
  onDelete?: (row: any) => void;
  onDataChange?: (newData: any[]) => void;
  searchPlaceholder?: string;
}
export function DataTable({
  columns,
  data,
  filters,
  onView,
  onEdit,
  onApprove,
  onDelete,
  onDataChange,
  searchPlaceholder = 'Search...'
}: DataTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Modal state
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'delete' | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const itemsPerPage = 10;
  const filteredData = data.filter(row => Object.values(row).some(value => String(value).toLowerCase().includes(searchTerm.toLowerCase())));
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);
  const handleAction = (action: 'view' | 'edit' | 'delete', row: any) => {
    if (onDataChange) {
      setSelectedRow(row);
      setEditFormData({
        ...row
      });
      setModalMode(action);
    } else {
      // Fallback to legacy callbacks
      if (action === 'view' && onView) onView(row);
      if (action === 'edit' && onEdit) onEdit(row);
      if (action === 'delete' && onDelete) onDelete(row);
    }
  };
  const handleSaveEdit = () => {
    if (onDataChange && selectedRow) {
      const newData = data.map(item => item === selectedRow ? editFormData : item);
      onDataChange(newData);
      setModalMode(null);
    }
  };
  const handleConfirmDelete = () => {
    if (onDataChange && selectedRow) {
      const newData = data.filter(item => item !== selectedRow);
      onDataChange(newData);
      setModalMode(null);
    }
  };
  return <div className="space-y-4 relative" data-id="element-46">
      {/* Search and Filters */}
      <div className="flex items-center justify-between gap-4" data-id="element-47">
        <div className="flex items-center gap-4 flex-1" data-id="element-48">
          <div className="flex-1 relative max-w-md" data-id="element-49">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-50" />
            <input type="text" placeholder={searchPlaceholder} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-51" />
          </div>
          {filters && filters.map(filter => <select key={filter.key} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-52">
                <option value="" data-id="element-53">{filter.label}</option>
                {filter.options.map(option => <option key={option} value={option} data-id="element-54">
                    {option}
                  </option>)}
              </select>)}
        </div>
        <div className="flex items-center gap-2" data-id="element-55">
          <button onClick={() => alert('Exporting to Excel...')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-56">
            <DownloadIcon size={16} data-id="element-57" />
            Export Excel
          </button>
          <button onClick={() => alert('Exporting to PDF...')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-58">
            <DownloadIcon size={16} data-id="element-59" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-60">
        <div className="overflow-x-auto" data-id="element-61">
          <table className="w-full" data-id="element-62">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-63">
              <tr data-id="element-64">
                {columns.map(column => <th key={column.key} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-65">
                    {column.label}
                  </th>)}
                {(onView || onEdit || onApprove || onDelete || onDataChange) && <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-66">
                    Actions
                  </th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-67">
              {paginatedData.map((row, idx) => <tr key={idx} className="hover:bg-gray-50 transition-colors" data-id="element-68">
                  {columns.map(column => <td key={column.key} className="px-6 py-4 text-sm text-gray-900" data-id="element-69">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>)}
                  {(onView || onEdit || onApprove || onDelete || onDataChange) && <td className="px-6 py-4 text-sm" data-id="element-70">
                      <div className="flex items-center gap-2" data-id="element-71">
                        {(onView || onDataChange) && <button onClick={() => handleAction('view', row)} className="p-1.5 text-gray-600 hover:text-[#1B5E20] hover:bg-gray-100 rounded transition-colors" title="View" data-id="element-72">
                            <EyeIcon size={16} data-id="element-73" />
                          </button>}
                        {(onEdit || onDataChange) && <button onClick={() => handleAction('edit', row)} className="p-1.5 text-gray-600 hover:text-[#1B5E20] hover:bg-gray-100 rounded transition-colors" title="Edit" data-id="element-74">
                            <EditIcon size={16} data-id="element-75" />
                          </button>}
                        {onApprove && <button onClick={() => onApprove(row)} className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-gray-100 rounded transition-colors" title="Approve" data-id="element-76">
                            <CheckIcon size={16} data-id="element-77" />
                          </button>}
                        {(onDelete || onDataChange) && <button onClick={() => handleAction('delete', row)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded transition-colors" title="Delete" data-id="element-78">
                            <TrashIcon size={16} data-id="element-79" />
                          </button>}
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-80">
          <div className="text-sm text-gray-700" data-id="element-81">
            Showing {startIndex + 1}-
            {Math.min(startIndex + itemsPerPage, filteredData.length)} of{' '}
            {filteredData.length}
          </div>
          <div className="flex items-center gap-2" data-id="element-82">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-83">
              <ChevronLeftIcon size={16} data-id="element-84" />
            </button>
            {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded-lg ${currentPage === page ? 'bg-[#1B5E20] text-white' : 'border border-gray-300 hover:bg-gray-50'}`} data-id="element-85">
                {page}
              </button>)}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-86">
              <ChevronRightIcon size={16} data-id="element-87" />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {modalMode && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-88">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" data-id="element-89">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-90">
              <h3 className="text-lg font-semibold text-gray-900 capitalize" data-id="element-91">
                {modalMode} Record
              </h3>
              <button onClick={() => setModalMode(null)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-92">
                <XIcon size={20} data-id="element-93" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1" data-id="element-94">
              {modalMode === 'view' && <div className="space-y-4" data-id="element-95">
                  {columns.map(col => <div key={col.key} className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-3 last:border-0" data-id="element-96">
                      <div className="text-sm font-medium text-gray-500" data-id="element-97">
                        {col.label}
                      </div>
                      <div className="col-span-2 text-sm text-gray-900" data-id="element-98">
                        {col.render ? col.render(selectedRow[col.key], selectedRow) : String(selectedRow[col.key] || '-')}
                      </div>
                    </div>)}
                </div>}

              {modalMode === 'edit' && <div className="space-y-4" data-id="element-99">
                  {columns.map(col => <div key={col.key} data-id="element-100">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-101">
                        {col.label}
                      </label>
                      {/* Skip rendering complex objects or use simple text inputs */}
                      <input type="text" value={editFormData[col.key] || ''} onChange={e => setEditFormData({
                ...editFormData,
                [col.key]: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-102" />
                    </div>)}
                </div>}

              {modalMode === 'delete' && <div className="text-center py-4" data-id="element-103">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-104">
                    <TrashIcon className="text-red-600" size={32} data-id="element-105" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-106">
                    Confirm Deletion
                  </h4>
                  <p className="text-gray-500" data-id="element-107">
                    Are you sure you want to delete this record? This action
                    cannot be undone.
                  </p>
                </div>}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl" data-id="element-108">
              <button onClick={() => setModalMode(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-109">
                {modalMode === 'view' ? 'Close' : 'Cancel'}
              </button>
              {modalMode === 'edit' && <button onClick={handleSaveEdit} className="px-4 py-2 text-sm font-medium text-white bg-[#1B5E20] rounded-lg hover:bg-[#2E7D32]" data-id="element-110">
                  Save Changes
                </button>}
              {modalMode === 'delete' && <button onClick={handleConfirmDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-111">
                  Delete Record
                </button>}
            </div>
          </div>
        </div>}
    </div>;
}